import { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { Activity } from '../../types/schedule';
import { formatDate } from './chartPrimitives';

type SortKey =
  | 'code'
  | 'name'
  | 'start'
  | 'finish'
  | 'totalFloatDays'
  | 'originalDurationDays'
  | 'percentComplete'
  | 'status';

interface ActivityTableProps {
  activities: Activity[];
  selectedActivityId?: string | null;
  onSelectActivity: (activityId: string) => void;
}

const COLUMNS: { key: SortKey; label: string; align?: 'right' }[] = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Activity' },
  { key: 'status', label: 'Status' },
  { key: 'start', label: 'Start' },
  { key: 'finish', label: 'Finish' },
  { key: 'originalDurationDays', label: 'Dur (d)', align: 'right' },
  { key: 'totalFloatDays', label: 'Float (d)', align: 'right' },
  { key: 'percentComplete', label: '% Cmp', align: 'right' },
];

const ROW_HEIGHT = 30;
const VIEWPORT = 520;

/**
 * Sortable, virtualised activity table. Rows are the same selection surface
 * as the charts, so clicking here opens the same inspector.
 */
export function ActivityTable({
  activities,
  selectedActivityId,
  onSelectActivity,
}: ActivityTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('start');
  const [ascending, setAscending] = useState(true);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const sorted = useMemo(() => {
    const copy = [...activities];
    copy.sort((a, b) => {
      const direction = ascending ? 1 : -1;
      switch (sortKey) {
        case 'start':
        case 'finish': {
          const aTime = a[sortKey]?.getTime() ?? 0;
          const bTime = b[sortKey]?.getTime() ?? 0;
          return (aTime - bTime) * direction;
        }
        case 'totalFloatDays':
        case 'originalDurationDays':
        case 'percentComplete':
          return (a[sortKey] - b[sortKey]) * direction;
        default:
          return String(a[sortKey]).localeCompare(String(b[sortKey])) * direction;
      }
    });
    return copy;
  }, [activities, sortKey, ascending]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setAscending((prev) => !prev);
    else {
      setSortKey(key);
      setAscending(true);
    }
    scrollRef.current?.scrollTo?.({ top: 0 });
  };

  const firstVisible = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 6);
  const lastVisible = Math.min(
    sorted.length,
    Math.ceil((scrollTop + VIEWPORT) / ROW_HEIGHT) + 6
  );
  const visible = sorted.slice(firstVisible, lastVisible);

  if (activities.length === 0) {
    return (
      <div className="p-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
        No activities match the current filters.
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="overflow-auto"
      style={{ maxHeight: VIEWPORT }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <table className="data-table">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                onClick={() => toggleSort(column.key)}
                style={{ textAlign: column.align ?? 'left' }}
                aria-sort={
                  sortKey === column.key
                    ? ascending
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                <span className="inline-flex items-center gap-1">
                  {column.label}
                  {sortKey === column.key &&
                    (ascending ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    ))}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Spacer rows keep the scrollbar honest while only the visible
              window is actually in the DOM. */}
          {firstVisible > 0 && (
            <tr style={{ height: firstVisible * ROW_HEIGHT }} aria-hidden="true">
              <td colSpan={COLUMNS.length} />
            </tr>
          )}

          {visible.map((activity) => {
            const floatTone =
              activity.totalFloatDays < 0
                ? 'var(--status-fail)'
                : activity.totalFloatDays === 0
                  ? 'var(--mr-vivid-blue)'
                  : activity.totalFloatDays > 44
                    ? 'var(--status-warn)'
                    : undefined;

            return (
              <tr
                key={activity.id}
                data-selected={activity.id === selectedActivityId}
                onClick={() => onSelectActivity(activity.id)}
                style={{ height: ROW_HEIGHT }}
              >
                <td className="tnum" style={{ color: 'var(--text-primary)' }}>
                  {activity.code}
                </td>
                <td
                  style={{
                    color: 'var(--text-primary)',
                    maxWidth: 320,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={activity.name}
                >
                  {activity.isCritical && !activity.isComplete && (
                    <span
                      className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle"
                      style={{ background: 'var(--mr-vivid-blue)' }}
                      title="Critical"
                    />
                  )}
                  {activity.name}
                </td>
                <td>
                  <span
                    className={`chip ${
                      activity.isComplete
                        ? 'chip-pass'
                        : activity.isInProgress
                          ? 'chip-info'
                          : 'chip-na'
                    }`}
                  >
                    {activity.status}
                  </span>
                </td>
                <td className="tnum">{formatDate(activity.start)}</td>
                <td className="tnum">{formatDate(activity.finish)}</td>
                <td className="tnum" style={{ textAlign: 'right' }}>
                  {activity.originalDurationDays.toFixed(0)}
                </td>
                <td className="tnum" style={{ textAlign: 'right', color: floatTone }}>
                  {activity.totalFloatDays.toFixed(0)}
                </td>
                <td className="tnum" style={{ textAlign: 'right' }}>
                  {activity.percentComplete.toFixed(0)}
                </td>
              </tr>
            );
          })}

          {lastVisible < sorted.length && (
            <tr
              style={{ height: (sorted.length - lastVisible) * ROW_HEIGHT }}
              aria-hidden="true"
            >
              <td colSpan={COLUMNS.length} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
